import { newBinding, matches } from '@boba-cli/key';
import { WindowSizeMsg, KeyMsg } from '@boba-cli/tea';
import { Style } from '@boba-cli/chapstick';

// src/model.ts
var defaultKeyMap = {
  down: newBinding({ keys: ["j", "down", "ctrl+n"] }).withHelp("j/\u2193", "down"),
  up: newBinding({ keys: ["k", "up", "ctrl+p"] }).withHelp("k/\u2191", "up")
};
function defaultStyles() {
  return {
    selectedItem: new Style().foreground("#00ff00").bold(true),
    normalItem: new Style()
  };
}
function mergeStyles(overrides) {
  if (!overrides) {
    return defaultStyles();
  }
  return { ...defaultStyles(), ...overrides };
}

// src/messages.ts
var GetDirectoryListingMsg = class {
  constructor(items) {
    this.items = items;
  }
  _tag = "filetree-get-directory-listing";
};
var ErrorMsg = class {
  constructor(error) {
    this.error = error;
  }
  _tag = "filetree-error";
};

// src/fs.ts
function convertBytesToSizeString(size) {
  if (size < 1024) {
    return `${size}B`;
  }
  const units = ["K", "M", "G", "T"];
  let unitIndex = -1;
  let adjustedSize = size;
  while (adjustedSize >= 1024 && unitIndex < units.length - 1) {
    adjustedSize /= 1024;
    unitIndex++;
  }
  const unit = units[unitIndex];
  if (!unit) {
    return `${size}B`;
  }
  return `${adjustedSize.toFixed(1)}${unit}`;
}
function formatPermissions(mode, isDirectory) {
  const type = isDirectory ? "d" : "-";
  const owner = [
    mode & 256 ? "r" : "-",
    mode & 128 ? "w" : "-",
    mode & 64 ? "x" : "-"
  ].join("");
  const group = [
    mode & 32 ? "r" : "-",
    mode & 16 ? "w" : "-",
    mode & 8 ? "x" : "-"
  ].join("");
  const others = [
    mode & 4 ? "r" : "-",
    mode & 2 ? "w" : "-",
    mode & 1 ? "x" : "-"
  ].join("");
  return `${type}${owner}${group}${others}`;
}
function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}
function isPermissionError(error) {
  return typeof error === "object" && error !== null && "code" in error && (error.code === "EACCES" || error.code === "EPERM");
}
function getDirectoryListingCmd(filesystem, path, dir, showHidden) {
  return async () => {
    try {
      const resolvedDir = path.resolve(dir);
      const entries = await filesystem.readdir(resolvedDir, {
        withFileTypes: true
      });
      if (entries.length > 0 && typeof entries[0] === "string") {
        throw new Error(
          "Filesystem adapter must support withFileTypes option"
        );
      }
      const typedEntries = entries;
      const items = [];
      for (const entry of typedEntries) {
        if (!showHidden && entry.name.startsWith(".")) {
          continue;
        }
        const itemPath = path.join(resolvedDir, entry.name);
        try {
          const stats = await filesystem.stat(itemPath);
          const extension = entry.isDirectory() ? "" : path.extname(entry.name);
          const dateStr = formatDate(stats.mtime);
          const perms = formatPermissions(stats.mode, entry.isDirectory());
          const size = convertBytesToSizeString(stats.size);
          const details = `${dateStr} ${perms} ${size}`;
          items.push({
            name: entry.name,
            details,
            path: itemPath,
            extension,
            isDirectory: entry.isDirectory(),
            currentDirectory: resolvedDir,
            mode: stats.mode
          });
        } catch (error) {
          if (isPermissionError(error)) {
            continue;
          }
          throw error;
        }
      }
      items.sort((a, b) => {
        if (a.isDirectory !== b.isDirectory) {
          return a.isDirectory ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      });
      return new GetDirectoryListingMsg(items);
    } catch (error) {
      return new ErrorMsg(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  };
}

// src/model.ts
var FiletreeModel = class _FiletreeModel {
  /** Current cursor position */
  cursor;
  /** Array of directory items */
  files;
  /** Whether component is active and receives input */
  active;
  /** Key bindings */
  keyMap;
  /** Minimum viewport scroll position */
  min;
  /** Maximum viewport scroll position */
  max;
  /** Component height */
  height;
  /** Component width */
  width;
  /** Styles */
  styles;
  /** Current directory */
  currentDir;
  /** Whether to show hidden files */
  showHidden;
  /** Last error, if any */
  error;
  /** Filesystem adapter */
  filesystem;
  /** Path adapter */
  path;
  constructor(cursor, files, active, keyMap, min, max, height, width, styles, currentDir, showHidden, error, filesystem, path) {
    this.cursor = cursor;
    this.files = files;
    this.active = active;
    this.keyMap = keyMap;
    this.min = min;
    this.max = max;
    this.height = height;
    this.width = width;
    this.styles = styles;
    this.currentDir = currentDir;
    this.showHidden = showHidden;
    this.error = error;
    this.filesystem = filesystem;
    this.path = path;
  }
  /**
   * Creates a new filetree model.
   * @param options - Configuration options
   * @returns A new FiletreeModel instance
   * @public
   */
  static new(options) {
    const currentDir = options.currentDir ?? options.filesystem.cwd();
    const showHidden = options.showHidden ?? false;
    const keyMap = options.keyMap ?? defaultKeyMap;
    const styles = mergeStyles(options.styles);
    const width = options.width ?? 80;
    const height = options.height ?? 24;
    return new _FiletreeModel(
      0,
      // cursor
      [],
      // files
      true,
      // active
      keyMap,
      0,
      // min
      height - 1,
      // max
      height,
      width,
      styles,
      currentDir,
      showHidden,
      null,
      // error
      options.filesystem,
      options.path
    );
  }
  /**
   * Initializes the model and returns a command to load the directory.
   * @returns Command to load directory listing
   * @public
   */
  init() {
    return getDirectoryListingCmd(
      this.filesystem,
      this.path,
      this.currentDir,
      this.showHidden
    );
  }
  /**
   * Sets whether the component is active and receives input.
   * @param active - Whether component should be active
   * @returns Updated model
   * @public
   */
  setIsActive(active) {
    if (this.active === active) {
      return this;
    }
    return new _FiletreeModel(
      this.cursor,
      this.files,
      active,
      this.keyMap,
      this.min,
      this.max,
      this.height,
      this.width,
      this.styles,
      this.currentDir,
      this.showHidden,
      this.error,
      this.filesystem,
      this.path
    );
  }
  /**
   * Updates the model in response to a message.
   * @param msg - The message to handle
   * @returns Tuple of updated model and command
   * @public
   */
  update(msg) {
    if (msg instanceof GetDirectoryListingMsg) {
      const newMax = Math.max(
        0,
        Math.min(this.height - 1, msg.items.length - 1)
      );
      return [
        new _FiletreeModel(
          0,
          // reset cursor to top
          msg.items,
          this.active,
          this.keyMap,
          0,
          newMax,
          this.height,
          this.width,
          this.styles,
          this.currentDir,
          this.showHidden,
          null,
          // clear error
          this.filesystem,
          this.path
        ),
        null
      ];
    }
    if (msg instanceof ErrorMsg) {
      return [
        new _FiletreeModel(
          this.cursor,
          this.files,
          this.active,
          this.keyMap,
          this.min,
          this.max,
          this.height,
          this.width,
          this.styles,
          this.currentDir,
          this.showHidden,
          msg.error,
          this.filesystem,
          this.path
        ),
        null
      ];
    }
    if (msg instanceof WindowSizeMsg) {
      const newHeight = msg.height;
      const newWidth = msg.width;
      const newMax = Math.max(0, Math.min(newHeight - 1, this.files.length - 1));
      const maxValidCursor = Math.max(0, this.files.length - 1);
      const adjustedCursor = Math.min(
        Math.min(this.cursor, maxValidCursor),
        newMax
      );
      const newMin = Math.min(
        Math.max(0, adjustedCursor - (newHeight - 1)),
        adjustedCursor
      );
      return [
        new _FiletreeModel(
          adjustedCursor,
          this.files,
          this.active,
          this.keyMap,
          newMin,
          newMax,
          newHeight,
          newWidth,
          this.styles,
          this.currentDir,
          this.showHidden,
          this.error,
          this.filesystem,
          this.path
        ),
        null
      ];
    }
    if (!this.active) {
      return [this, null];
    }
    if (msg instanceof KeyMsg) {
      if (matches(msg, this.keyMap.down)) {
        if (this.files.length === 0) {
          return [this, null];
        }
        const nextCursor = Math.min(this.cursor + 1, this.files.length - 1);
        let nextMin = this.min;
        let nextMax = this.max;
        if (nextCursor > this.max) {
          nextMin = this.min + 1;
          nextMax = this.max + 1;
        }
        return [
          new _FiletreeModel(
            nextCursor,
            this.files,
            this.active,
            this.keyMap,
            nextMin,
            nextMax,
            this.height,
            this.width,
            this.styles,
            this.currentDir,
            this.showHidden,
            this.error,
            this.filesystem,
            this.path
          ),
          null
        ];
      }
      if (matches(msg, this.keyMap.up)) {
        if (this.files.length === 0) {
          return [this, null];
        }
        const nextCursor = Math.max(this.cursor - 1, 0);
        let nextMin = this.min;
        let nextMax = this.max;
        if (nextCursor < this.min && this.min > 0) {
          nextMin = this.min - 1;
          nextMax = this.max - 1;
        }
        return [
          new _FiletreeModel(
            nextCursor,
            this.files,
            this.active,
            this.keyMap,
            nextMin,
            nextMax,
            this.height,
            this.width,
            this.styles,
            this.currentDir,
            this.showHidden,
            this.error,
            this.filesystem,
            this.path
          ),
          null
        ];
      }
    }
    return [this, null];
  }
  /**
   * Renders the file tree view.
   * @returns Rendered string
   * @public
   */
  view() {
    if (this.error) {
      return `Error: ${this.error.message}`;
    }
    if (this.files.length === 0) {
      return "(empty directory)";
    }
    const lines = [];
    for (let i = this.min; i <= this.max && i < this.files.length; i++) {
      const item = this.files[i];
      if (!item) continue;
      const isSelected = i === this.cursor;
      const style = isSelected ? this.styles.selectedItem : this.styles.normalItem;
      const line = `${item.name}  ${item.details}`;
      lines.push(style.render(line));
    }
    const remainingLines = this.height - lines.length;
    for (let i = 0; i < remainingLines; i++) {
      lines.push("");
    }
    return lines.join("\n");
  }
  /**
   * Gets the currently selected file, if any.
   * @returns The selected DirectoryItem or null
   * @public
   */
  get selectedFile() {
    return this.files[this.cursor] ?? null;
  }
};

export { ErrorMsg, FiletreeModel, GetDirectoryListingMsg, convertBytesToSizeString, defaultKeyMap, defaultStyles, getDirectoryListingCmd, mergeStyles };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map